/* eslint-disable no-restricted-globals */
/* eslint-disable no-case-declarations */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { QueryBuilder } from '@cubejs-client/react';
import numeral from 'numeral';
import {CircleChart, BarChartLegacy} from 'shared/components';
import CircularProgress from '@mui/material/CircularProgress';
import Card from '@mui/material/Card';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import AppsIcon from '@mui/icons-material/Apps';
import InfoIcon from '@mui/icons-material/Info';
import CancelIcon from '@mui/icons-material/Cancel';
import Divider from '@mui/material/Divider';
import DataTableComponent from '../DataTableComponent';
import { getCubeJsApi } from '../../utils/index';
import useStyles from './styles';

const CardTypes = {
  DATA_TABLE: 'data-table',
  CHART: 'chart',
  NUMBER: 'number',
  CIRCLE_CHART: 'circle-chart'
};

const ResourceIdTypes = {
  COMPLIANCE: 'compliance',
  LEVERAGE: 'leverage',
  FEEDBACK: 'feedback',
  DATA_QUALITY: 'data_quality',
  SROI: 'sroi',
  TOTAL_INV_AMT: 'total_inv_amt',
  TOTAL_REACHED: 'total_reached',
  TOTAL_SI: 'total_si',
  RATINGS: 'ratings'
};

const CardComponent = ({
  query,
  type,
  title,
  info,
  resourceId,
  description,
  noBorder,
  noIcons,
  onPressModalOpen,
  ratingCategories
}) => {
  const classes = useStyles();
  const [showInfo, setShowInfo] = useState(false);
  const showIcons = !noIcons;

  const cubeTokenReady = useSelector(
    state => !!(state.profile.me && state.profile.me.cubejsToken)
  );

  const getDataQuality = resultSet => {
    let rClass;
    let rClassType;
    let rCount;
    const chartData = { M: 0, E: 0, G: 0, O: 0 };
    resultSet.loadResponse.data.forEach(item => {
      rClass = item['Results.classification'];
      rCount = item['Claims.count'];
      if (rClass) {
        rClassType = rClass.charAt(0);
        chartData[rClassType] += rCount;
      }
      chartData.O += rCount;
    });
    const evidencePct = Math.round(
      ((chartData.M + chartData.E) /
        (chartData.M + chartData.E + chartData.G)) *
        100
    );
    const data = [
      { value: chartData.M, name: 'Measured', color: '#006400' },
      { value: chartData.E, name: 'Estimate', color: '#32cd32' },
      { value: chartData.G, name: 'Guess', color: '#ff4500' },
      { value: chartData.O, name: 'Other', color: '#243377' }
    ];
    return {
      data,
      label: `${isNaN(evidencePct) ? 0 : evidencePct}%`
    };
  };

  const getRatingsDataSet = (results, categories) => {
    const dataSet = { ...results };
    if (dataSet !== undefined && dataSet.loadResponse !== undefined) {
      dataSet.loadResponse.data.forEach((value, index) => {
        const category = value['FunderRatings.funderRatingCategory'];
        const ratingValue = value['FunderRatings.value'];
        if (categories !== undefined) {
          const selectedCat = categories.filter(cat => cat.id === category);
          if (selectedCat && selectedCat.length > 0) {
            dataSet.loadResponse.data[index][
              'FunderRatings.funderRatingCategory'
            ] = selectedCat[0].name;
            selectedCat[0].options.values.forEach(opt => {
              if (opt.value === ratingValue) {
                dataSet.loadResponse.data[index]['FunderRatings.value'] =
                  opt.criteria;
              }
            });
          }
        }
      });
      return dataSet;
    }
    return undefined;
  };

  const renderChartByType = resultSet => {
    switch (type) {
      case CardTypes.DATA_TABLE:
        if (resourceId === ResourceIdTypes.RATINGS) {
          const ratingsDataSet = getRatingsDataSet(resultSet, ratingCategories);
          if (ratingsDataSet !== undefined) {
            return <DataTableComponent resultSet={ratingsDataSet} />;
          }
        }
        return <DataTableComponent resultSet={resultSet} />;
      case CardTypes.CHART:
        return <BarChartLegacy resultSet={resultSet} />;
      case CardTypes.CIRCLE_CHART:
        if (resultSet) {
          const { data: dataquality } = getDataQuality(resultSet);
          return <CircleChart data={dataquality} />;
        }
        return null;
      default:
        return null;
    }
  };

  const toggleInfo = () => {
    setShowInfo(!showInfo);
  };

  const renderLabel = resultSet => {
    if (resultSet) {
      const { data } = resultSet.loadResponse;
      switch (resourceId) {
        case ResourceIdTypes.COMPLIANCE:
          const totalCnt = data.reduce((acc, item) => {
            let total = acc;
            if (item) {
              total += item['Reports.count'];
            }
            return total;
          }, 0);
          const finalizedCnt = data.filter(
            item => item['Reports.status'] === 'Finalized'
          )[0];
          const publishedCnt = data.filter(
            item => item['Reports.status'] === 'Published'
          )[0];
          if (finalizedCnt && publishedCnt) {
            const compliancePct = Math.round(
              ((finalizedCnt['Reports.count'] + publishedCnt['Reports.count']) /
                totalCnt) *
                100
            );
            return <h2 className={classes.label}>{`${compliancePct}%`}</h2>;
          }
          return <h2 className={classes.label}>0%</h2>;
        case ResourceIdTypes.LEVERAGE:
          const leveragePercentage = data[0]['Claims.leveragePercentage'];
          const leverageLabel = !isNaN(leveragePercentage)
            ? leveragePercentage.toFixed(2)
            : 0;
          return (
            <h2 className={classes.label}>{`${
              leverageLabel > 1 ? leverageLabel : '<1'
            }%`}</h2>
          );
        case ResourceIdTypes.FEEDBACK:
          const reportsCount = data[0]['Reports.count'] || 0;
          return <h2 className={classes.label}>{reportsCount}</h2>;
        case ResourceIdTypes.DATA_QUALITY:
          if (resultSet) {
            const { label: dataqualityLabel } = getDataQuality(resultSet);
            return <h2 className={classes.label}>{dataqualityLabel}</h2>;
          }
          return null;
        case ResourceIdTypes.SROI:
          const sroiLabel = Math.floor(data[0]['Claims.SROI'] || 0);
          return (
            <div>
              <h2 className={classes.label}>
                {numeral(sroiLabel).format(0, 0)}
              </h2>
              <p>Success Per $10K</p>
            </div>
          );
        case ResourceIdTypes.TOTAL_INV_AMT:
          const invAmount = data[0]['Investments.totalAmount'] || 0;
          return (
            <h2 className={classes.label}>
              ${numeral(invAmount).format(0, 0)}
            </h2>
          );
        case ResourceIdTypes.TOTAL_REACHED:
          const totalReached = data[0]['Claims.totalClaim'] || 0;
          return (
            <h2 className={classes.label}>
              {numeral(Math.ceil(totalReached)).format(0, 0)}
            </h2>
          );
        case ResourceIdTypes.TOTAL_SI:
          const totalSi = data[0]['Claims.totalClaim'] || 0;
          return (
            <h2 className={classes.label}>
              {numeral(Math.ceil(totalSi)).format(0, 0)}
            </h2>
          );
        default:
          return null;
      }
    }
    return null;
  };

  const getDescription = resultSet => {
    if (resultSet) {
      switch (resourceId) {
        case ResourceIdTypes.TOTAL_INV_AMT:
          const investmentsCount =
            resultSet.loadResponse.data[0]['Investments.count'] || 0;
          return description.replace('{}', investmentsCount);
        default:
          return description;
      }
    }
    return description;
  };

  const handleOnPressOpenModal = () => {
    onPressModalOpen({
      query,
      type,
      title,
      info,
      resourceId,
      description,
      noBorder: true,
      noIcons: true,
      ratingCategories
    });
  };

  if (!cubeTokenReady) {
    return null;
  }

  return (
    <QueryBuilder
      query={query}
      cubejsApi={getCubeJsApi()}
      render={({ resultSet, measures }) => (
        <Card className={[classes.card, noBorder && classes.noBorder]}>
          {showIcons && <AppsIcon className={classes.sort} />}
          <h2 className={classes.cardTitle}>{title}</h2>
          {showIcons && onPressModalOpen && (
            <AspectRatioIcon
              onClick={handleOnPressOpenModal}
              className={classes.spand}
            />
          )}
          {renderLabel(resultSet)}
          {description && (
            <p>
              <Divider className={classes.divider} variant="middle" />
              {resultSet && (
                <h3 className={classes.cardDescription}>
                  {getDescription(resultSet)}
                </h3>
              )}
            </p>
          )}
          {measures.length > 0 ? (
            renderChartByType(resultSet)
          ) : (
            <CircularProgress />
          )}
          {showIcons && info && (
            <InfoIcon onClick={toggleInfo} className={classes.info} />
          )}
          {showInfo && (
            <div className={classes.infoOverlay}>
              <div className={classes.overlayOpacity} />
              <span className={classes.textContainer}>{info}</span>
              <CancelIcon onClick={toggleInfo} className={classes.info} />
            </div>
          )}
        </Card>
      )}
    />
  );
};

CardComponent.propTypes = {
  query: PropTypes.object,
  type: PropTypes.string,
  title: PropTypes.string,
  info: PropTypes.string,
  resourceId: PropTypes.string,
  description: PropTypes.string,
  noBorder: PropTypes.bool,
  noIcons: PropTypes.bool,
  onPressModalOpen: PropTypes.func,
  ratingCategories: PropTypes.array,
};

export default CardComponent;
